import type {
  MutationOptions,
  QueryObserverOptions,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  getUserColumnPreferences,
  getUserProfile,
  putUserProfile,
  uploadUserColumnPreferences,
} from 'api/user';
import type {
  GetUserProfileResponseDTO,
  UpdateUserRequestBodyDTO,
  UploadUserColumnPreferences,
} from 'declarations/dto/user';
import { RequestQueryKeysE, RequestStaleTimeE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';

type UseUpdateUserParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, UpdateUserRequestBodyDTO>['onSuccess'];
};

type UseFetchUserPreferencesParamsT = {
  onSuccess: QueryObserverOptions<string, ApiErrorI>['onSuccess'];
  onError: QueryObserverOptions<string, ApiErrorI>['onError'];
};

export const useFetchUser = (
  idToken: string | null
): UseQueryResult<GetUserProfileResponseDTO, ApiErrorI> =>
  useQuery<GetUserProfileResponseDTO, ApiErrorI>([RequestQueryKeysE.FetchUser], getUserProfile, {
    enabled: !!idToken,
    staleTime: RequestStaleTimeE.UserProfile,
  });

export const useUpdateUser = ({
  onSuccess,
}: UseUpdateUserParamsT): UseMutationResult<void, ApiErrorI, UpdateUserRequestBodyDTO> =>
  useMutation<void, ApiErrorI, UpdateUserRequestBodyDTO>(
    [RequestQueryKeysE.UpdateUser],
    userProfileData => putUserProfile(userProfileData),
    { onSuccess }
  );

export const useFetchUserColumnPreferences = ({
  onSuccess,
}: UseFetchUserPreferencesParamsT): UseQueryResult<string, ApiErrorI> => {
  return useQuery<string, ApiErrorI>(
    [RequestQueryKeysE.FetchUserColumnPreferences],
    getUserColumnPreferences,
    { onSuccess, enabled: !localStorage.getItem('visibleColumns') }
  );
};

export const useUpdateUserColumnPreferences = (): UseMutationResult<
  void,
  ApiErrorI,
  UploadUserColumnPreferences
> =>
  useMutation<void, ApiErrorI, UploadUserColumnPreferences>(
    [RequestQueryKeysE.UpdateUserColumnPreferences],
    userColumnPreferencesData => uploadUserColumnPreferences(userColumnPreferencesData)
  );
